import React, { useMemo } from 'react';
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { MultiSelect, MultiSelectOption } from '../../components/shared/MultiSelect';
import { ScrollArea } from '../../components/ui/scroll-area';
import { CloudAnalyticsFact } from '../../services/cloudAssetService';
import {
    CloudDateRange, CloudDimension, CloudSelection, DIMENSION_LABELS,
    applySelection, factLabel,
} from './cloudAnalyticsModel';

/** Dimensions the drawer edits, in the order they read best. */
const FILTER_DIMENSIONS: CloudDimension[] = [
    'provider', 'tier', 'environment', 'resourceType',
    'region', 'state', 'lifecycle', 'department', 'owner',
];

/**
 * Options for one dimension, taken from the fact table itself.
 *
 * Derived from the rows rather than the master tables so the list only ever
 * offers values that exist in the caller's scope — a department with no cloud
 * resources is not a filter worth offering, and picking it would blank every
 * chart with no way to tell why.
 */
function optionsFor(facts: CloudAnalyticsFact[], dimension: CloudDimension): MultiSelectOption[] {
    const seen = new Map<string, string>();
    facts.forEach((fact) => {
        const key = String(fact[dimension]);
        if (!seen.has(key)) seen.set(key, factLabel(fact, dimension));
    });
    return [...seen.entries()]
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}

interface CloudAnalyticsFiltersProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    facts: CloudAnalyticsFact[];
    draft: CloudSelection;
    onDraftChange: (selection: CloudSelection) => void;
    dateDraft: CloudDateRange;
    onDateDraftChange: (range: CloudDateRange) => void;
    onApply: () => void;
    onClear: () => void;
}

/**
 * Filter drawer for Analytics → Cloud (User Story 101).
 *
 * Writes the same `CloudSelection` that clicking a chart slice does (Story
 * 100), so the drawer and the charts are two ways into one filter rather than
 * two competing ones — a selection made by clicking shows up here, and vice
 * versa. Multi-select throughout, because that selection model genuinely holds
 * several keys per dimension.
 */
export const CloudAnalyticsFilters: React.FC<CloudAnalyticsFiltersProps> = ({
    open, onOpenChange, facts, draft, onDraftChange, dateDraft, onDateDraftChange,
    onApply, onClear,
}) => {
    // Each dimension's options respect the *other* dimensions' selections, so
    // the drawer cannot offer a combination that matches nothing — the same
    // exclude-own-dimension rule the charts follow.
    const options = useMemo(() => {
        const map = {} as Record<CloudDimension, MultiSelectOption[]>;
        FILTER_DIMENSIONS.forEach((dimension) => {
            map[dimension] = optionsFor(applySelection(facts, draft, dimension), dimension);
        });
        return map;
    }, [facts, draft]);

    const dateError = dateDraft.startDate && dateDraft.endDate && dateDraft.endDate < dateDraft.startDate
        ? 'End date is before the start date.'
        : null;

    const set = (dimension: CloudDimension, values: string[]) => {
        const next: CloudSelection = { ...draft };
        if (values.length === 0) delete next[dimension];
        else next[dimension] = values;
        onDraftChange(next);
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="sm:max-w-lg">
                <SheetHeader>
                    <SheetTitle>Filter Cloud</SheetTitle>
                </SheetHeader>

                <ScrollArea className="flex-1 min-h-0">
                    <div className="space-y-6 px-6 py-5">
                        <p className="text-xs text-muted-foreground">
                            Filters apply instantly across every chart, KPI and table on this tab —
                            the same filter you get by clicking a slice.
                        </p>
                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Period</h4>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="cfilter-start">Registered from</Label>
                                    <Input
                                        id="cfilter-start" type="date"
                                        value={dateDraft.startDate ?? ''}
                                        onChange={(e) => onDateDraftChange({ ...dateDraft, startDate: e.target.value || undefined })}
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="cfilter-end">Registered to</Label>
                                    <Input
                                        id="cfilter-end" type="date"
                                        value={dateDraft.endDate ?? ''}
                                        onChange={(e) => onDateDraftChange({ ...dateDraft, endDate: e.target.value || undefined })}
                                    />
                                </div>
                            </div>
                            {dateError && <p className="text-xs text-destructive">{dateError}</p>}
                            <p className="text-xs text-muted-foreground">
                                When the resource was entered in the register — no provider reports a
                                creation date to this system. Either end may be left open.
                            </p>
                        </div>

                        <div className="space-y-4">
                            <h4 className="text-sm font-semibold">Attributes</h4>
                            {FILTER_DIMENSIONS.map((dimension) => (
                                <div key={dimension} className="space-y-1.5">
                                    <Label htmlFor={`cfilter-${dimension}`}>{DIMENSION_LABELS[dimension]}</Label>
                                    <MultiSelect
                                        id={`cfilter-${dimension}`}
                                        options={options[dimension]}
                                        selected={draft[dimension] ?? []}
                                        onChange={(values) => set(dimension, values)}
                                        placeholder={`Any ${DIMENSION_LABELS[dimension].toLowerCase()}`}
                                        searchPlaceholder="Search…"
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </ScrollArea>

                <SheetFooter>
                    <Button variant="outline" onClick={onClear} className="cursor-pointer">Clear all</Button>
                    <Button
                        onClick={() => { onApply(); onOpenChange(false); }}
                        disabled={!!dateError}
                        className="cursor-pointer"
                    >
                        Apply Filters
                    </Button>
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
